import {
  Button,
  Chevron,
  Div,
  EditPenIcon,
  H3,
  Icon,
  Modal,
  P,
  Radio,
  Separator,
  TrashIconRed,
  displayCentsInDollars,
  lightTheme,
} from '@vgrubs/components';
import {
  ModifierGroupItemV2Response,
  ModifierItemV2Response,
} from 'components/src/types/menu.types';
import { Dispatch, SetStateAction, useRef, useState } from 'react';
import { Collapsible } from '../../MenyStyled';
import { ProviderPriceChips } from '../Modifiers/ModifierComponents';

export const AddModifierGroupComponent = ({
  item,
  edit,
  setModifierGroups,
  modifierGroups,
  isPos,
  handleEditModifier,
  handleEditModifierGroup,
  allowedToEditMenuPrice,
}: {
  item: ModifierGroupItemV2Response;
  edit?: boolean;
  setModifierGroups?: Dispatch<SetStateAction<ModifierGroupItemV2Response[]>>;
  modifierGroups?: ModifierGroupItemV2Response[];
  isPos?: boolean;
  handleEditModifier?: (item: ModifierItemV2Response) => void;
  handleEditModifierGroup?: (group: ModifierGroupItemV2Response) => void;
  allowedToEditMenuPrice?: boolean;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [elementHeight] = useState(ref?.current?.clientHeight);

  const [expanded, setExpanded] = useState(false);
  const [areYouSureModal, setAreYouSureModal] = useState(false);

  function isSelected(item: ModifierGroupItemV2Response) {
    return modifierGroups?.some((location) => location.id === item.id);
  }

  const handleSelect = (newItem: ModifierGroupItemV2Response) => {
    const index = modifierGroups?.findIndex((item) => item === newItem);
    if (index === -1) {
      setModifierGroups?.((prevIds) => [...prevIds, newItem]);
    } else {
      setModifierGroups?.((prevIds) =>
        prevIds.filter((item) => item !== newItem)
      );
    }
  };

  return (
    <Div $mb="8">
      <Div
        $box
        $p="0 20px"
        $pSM="0 16px"
        $minHeight="54px"
        $flex
        $alignCenter
        $between
        $borderRadius="16px"
        $gap="16px"
        $gapSM="8px"
      >
        <Div $pointer $flex $alignCenter $gap="8px">
          {!edit && (
            <div onClick={() => handleSelect(item)}>
              <Radio checked={isSelected(item) ?? false} />
            </div>
          )}

          <P $bold $m="0" $left onClick={() => setExpanded((prev) => !prev)}>
            {item.name}
          </P>
        </Div>
        <Div $flex $alignCenter $gap="16px">
          {!isPos && edit && handleEditModifierGroup && (
            <Icon
              $shadow
              $pointer
              onClick={() => handleEditModifierGroup(item)}
            >
              <EditPenIcon />
            </Icon>
          )}
          {!isPos && edit && (
            <Icon $pointer onClick={() => setAreYouSureModal(true)}>
              <TrashIconRed />
            </Icon>
          )}
          <Icon
            $pointer
            $rotate={expanded ? '360deg' : '180deg'}
            onClick={() => setExpanded((prev) => !prev)}
            $size="20"
          >
            <Chevron />
          </Icon>
        </Div>
      </Div>
      <Collapsible
        expanded={expanded}
        elementHeight={elementHeight}
        $mb="0"
        $mt="0"
      >
        <div ref={ref}>
          {item?.options?.map((item, i) => {
            return (
              <ModifierComponent
                item={item}
                key={i}
                isPos={isPos}
                index={i}
                handleEditModifier={handleEditModifier}
                handleEditModifierGroup={handleEditModifierGroup}
                allowedToEditMenuPrice={allowedToEditMenuPrice}
              />
            );
          })}
          {item?.options?.length === 0 ? (
            <P $m="0" $center>
              No modifiers
            </P>
          ) : null}
        </div>
      </Collapsible>
      <Modal
        isOpen={areYouSureModal}
        closeModal={() => {
          setAreYouSureModal(false);
        }}
        logo={false}
        zIndex={110}
        overlayBorderRadius={30}
      >
        <H3 $mb="24" $color={lightTheme.primary}>
          Are you sure
        </H3>
        <P $mb="40">Are you sure you want to remove this modifier group?</P>

        <Div $flex $gap="16px">
          <Button
            onClick={() => {
              handleSelect(item);
              setAreYouSureModal(false);
            }}
            $w="100%"
            $selected
          >
            Remove
          </Button>
          <Button
            $selected
            $gray
            $w="100%"
            onClick={() => setAreYouSureModal(false)}
          >
            Cancel
          </Button>
        </Div>
      </Modal>
    </Div>
  );
};

export const ModifierComponent = ({
  item,
  isPos,
  index,
  handleEditModifier,
  handleEditModifierGroup,
  allowedToEditMenuPrice,
}: {
  item: ModifierItemV2Response;
  isPos?: boolean;
  index: number;
  handleEditModifier?: (item: ModifierItemV2Response) => void;
  handleEditModifierGroup?: (group: ModifierGroupItemV2Response) => void;
  allowedToEditMenuPrice?: boolean;
}) => {
  return (
    <div>
      {index > 0 && <Separator $color={lightTheme.mediumGray} />}
      <Div>
        <Div $minHeight="48px" $p="4px 20px" $flex $between $alignCenter>
          <P $m="0">{item.name}</P>
          <Div $flex $alignCenter $gap="16px" $gapSM="8px">
            <ProviderPriceChips customizedPrices={item.customizedPrices} />

            <P $m="0">{displayCentsInDollars(item.price)}</P>
            {isPos && handleEditModifier && (
              <Icon
                $disabled={!allowedToEditMenuPrice}
                $shadow
                $pointer
                onClick={() =>
                  allowedToEditMenuPrice && handleEditModifier(item)
                }
              >
                <EditPenIcon />
              </Icon>
            )}
          </Div>
        </Div>
        {item?.modifierGroups?.length > 0 && (
          <Div $p="0 0 0 32px" $pSM="0 0 0 24px">
            {item?.modifierGroups?.map((modifierGroup, i) => (
              <AddModifierGroupComponent
                item={modifierGroup}
                key={i}
                edit
                isPos={isPos}
                handleEditModifier={handleEditModifier}
                handleEditModifierGroup={handleEditModifierGroup}
                allowedToEditMenuPrice={allowedToEditMenuPrice}
              />
            ))}
          </Div>
        )}
      </Div>
    </div>
  );
};
